import { useForm } from "react-hook-form";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import FilePond from "@/components/ui/file-pond";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useState } from "react";
import { useCandidateQuery } from "@/queries/candidate/candidate";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PopoverTooltip } from "@/components/ui/tooltip";
import { CircleAlert } from "lucide-react";
import ImageUploadPreview from "@/components/ui/image-upload-preview";

import {
  candidateNameLocationTitleFormSchema,
  CandidateNameLocationTitleFormSchema,
} from "./schema";

import { useUpdateCandidateProfile } from "@/mutations/candidate/profile/updateProfile";

const CandidateNameLocationTitleEditFormDialog = ({
  profileId,
}: {
  profileId: string;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { candidate } = useCandidateQuery(profileId);
  const { updateCandidateProfile, isUpdatingCandidateProfile } =
    useUpdateCandidateProfile(profileId, () => setIsOpen(false));

  const form = useForm<CandidateNameLocationTitleFormSchema>({
    resolver: zodResolver(candidateNameLocationTitleFormSchema),
    mode: "onChange",
    defaultValues: {
      name: candidate?.name,
      titleAndEmployer: candidate?.titleAndEmployer,
      location: candidate?.location,
      yearsOfExperience: candidate?.yearsOfExperience,
      yearsOfHealthcareExperience: candidate?.yearsOfHealthcareExperience,
      isPublic: candidate?.isPublic,
    },
  });

  useEffect(() => {
    form.reset({
      name: candidate?.name,
      titleAndEmployer: candidate?.titleAndEmployer,
      location: candidate?.location,
      yearsOfExperience: candidate?.yearsOfExperience,
      yearsOfHealthcareExperience: candidate?.yearsOfHealthcareExperience,
      isPublic: candidate?.isPublic,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, candidate]);

  const onSubmit = (data: CandidateNameLocationTitleFormSchema) => {
    updateCandidateProfile({
      emails: candidate?.emails ?? [],
      ...data,
    });
  };

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button variant="link" className="edit-text self-end">
          Edit
        </Button>
      </DialogTrigger>
      <DialogContent className="w-full sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>Edit Personal Information</DialogTitle>
          </div>
          <DialogDescription className="sr-only">
            Update your personal information below.
          </DialogDescription>
        </DialogHeader>
        <ScrollArea className="sm:max-h-[calc(100vh-10rem)] md:max-h-[70vh]">
          <Form {...form}>
            <form
              className="space-y-4 w-full"
              onSubmit={(e) => {
                e.preventDefault();
                void form.handleSubmit((data) => onSubmit(data))();
              }}
            >
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel required>Name</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="profilePicture"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Profile Picture</FormLabel>
                    <ImageUploadPreview
                      newImageFile={field.value}
                      oldImageLink={candidate?.profilePictureLink}
                    />
                    <FormControl>
                      <FilePond
                        value={field.value ? [field.value] : []}
                        accept={"image/*"}
                        maxFileSizeKiloBytes={10240}
                        onChange={(files) => {
                          field.onChange(files[0]);
                        }}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Location</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="titleAndEmployer"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Headline</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="yearsOfExperience"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Years of Experience</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" min={0} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="yearsOfHealthcareExperience"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Years of Healthcare Experience</FormLabel>
                    <FormControl>
                      <Input {...field} type="number" min={0} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="isPublic"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex flex-row gap-1">
                      Profile Visibility
                      <PopoverTooltip content="Make your profile public to allow hiring teams to view your profile automatically when there's a strong match">
                        <CircleAlert className="cursor-pointer" size={12} />
                      </PopoverTooltip>
                    </FormLabel>
                    <FormControl>
                      <Select
                        name={field.name}
                        value={field.value.toString()}
                        onValueChange={(val) => field.onChange(val === "true")}
                        disabled={field.disabled}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder={"Select one"} />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="true">Public</SelectItem>
                          <SelectItem value="false">Private</SelectItem>
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="w-full flex justify-between">
                <Button
                  variant="outline"
                  className="w-fit"
                  type="button"
                  onClick={() => setIsOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="secondary"
                  className="w-fit"
                  type="submit"
                  disabled={
                    !form.formState.isValid || isUpdatingCandidateProfile
                  }
                >
                  Save
                </Button>
              </div>
            </form>
          </Form>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default CandidateNameLocationTitleEditFormDialog;
